import { formatMillisecondsAsMMSS } from '@components/ScreenerResults/utils';
import { compact } from '@components/utils';

export const consolidateCards = (
  arr: NonNullable<ScreenerResponseAnswerValue<'card_sort'>>['results']
): NonNullable<ScreenerResponseAnswerValue<'card_sort'>>['results'] =>
  Object.values(
    arr.reduce((acc, curr) => {
      if (!acc[curr.name]) {
        acc[curr.name] = { name: curr.name, cards: [] };
      }

      acc[curr.name].cards.push(...curr.cards);

      return acc;
    }, {})
  );

export const getUniqueCategories = (answers: NonNullable<ScreenerResponseAnswerValue<'card_sort'>>[]): string[] => {
  const uniqueCategories = compact(
    answers.reduce((acc, answer) => {
      return [...acc, ...answer.results.map(({ name }) => name)];
    }, [] as string[])
  );

  return Array.from(new Set(uniqueCategories)).sort();
};

export const getMatrixValue = (showPercentage, value, totalAnswers) => {
  if (!value) return '';

  if (showPercentage) {
    if (totalAnswers === 0) return '0 %';

    return `${Math.round((value / totalAnswers) * 100)} %`;
  }

  return value;
};

export const getStyles = (value: number) => {
  if (value > 80) {
    return { textColor: 'text-white', backgroundColor: '#12102F' };
  } else if (value > 60) {
    return { textColor: 'text-white', backgroundColor: '#5850EC' };
  } else if (value > 40) {
    return { textColor: 'text-white', backgroundColor: '#76A9FA' };
  } else if (value > 0) {
    return { textColor: 'text-gray-700', backgroundColor: '#F0F5FF' };
  } else {
    return { textColor: 'text-white', backgroundColor: '#FFFFFF' };
  }
};

export const getCsvDataForAgreementMatrix = (
  cards: string[],
  categories: string[],
  answers: NonNullable<ScreenerResponseAnswerValue<'card_sort'>>[],
  mergedCategories: { name: string; categories: string[] }[] = [],
  showPercentage: boolean = true
) => {
  // Build headers - first column is empty for card names, then category names
  const headers = [
    { label: '', key: 'card' },
    ...categories.map((category) => {
      const isMerged = mergedCategories.some((merged) => merged.name === category);
      const label = isMerged ? `${category} (merged)` : category;
      return { label, key: category };
    })
  ];

  // Build data rows
  const data = cards.map((card) => {
    const row: Record<string, string> = { card };

    categories.forEach((category) => {
      // Count how many answers placed this card in this category
      const totalCardsInCategory = answers.reduce((acc, answer) => {
        if (answer.results.find(({ name }) => name === category)?.cards.includes(card)) {
          return acc + 1;
        }
        return acc;
      }, 0);

      // Format as percentage or raw count
      const value = showPercentage
        ? `${Math.round((totalCardsInCategory / answers.length) * 100)}%`
        : totalCardsInCategory.toString();

      row[category] = totalCardsInCategory === 0 ? '0' : value;
    });

    return row;
  });

  return { headers, data };
};

export const getCsvDataForResponsesTable = (answers: NonNullable<ScreenerResponseAnswerValue<'card_sort'>>[]) => {
  return answers.map((answer) => {
    const totalCards = answer.results.reduce((acc, { cards }) => acc + cards.length, 0);
    const totalCategories = answer.results.length;

    // Create a simple response summary showing categories and their card names
    const responseText = answer.results.map(({ name, cards }) => `${name} (${cards.join(', ')})`).join(', ');

    return {
      name: answer.participation_name || 'Unnamed',
      complete: answer.completed ? 'Yes' : 'No',
      cards_categories: `${totalCards || '-'} / ${totalCategories || '-'}`,
      total_time: answer.time_in_task ? formatMillisecondsAsMMSS(answer.time_in_task) : '00:00',
      response: responseText
    };
  });
};
